import { calculate } from '@calculations/price/strategies/raf-fixed-market/calculate';
import { calculateTotal } from '@calculations/price/utilities/total';
import { SnapRoundingOptionsCustom } from '@calculations/round';
import { PriceStrategyConfigWithRelations } from '@snap-types/models/price-strategy-config';
import { SnapSuggestionStatus } from '@snap-types/models/sort-status';
import {
  getQuoteLineOrThrow,
  getQuotePriceOrThrow,
} from '@ui/features/quote-builder/state/utilities/action-guards';
import { QuoteBuilderStateType } from '@ui/features/quote-builder/types/state';

import { applyQuotePriceRecalcResult } from '../utilities/apply-quote-price-recalc-result';
import { getAllowFallback } from '../utilities/get-allow-fallback';

export function onPartProductOptionChange(
  state: QuoteBuilderStateType,
  params: {
    quoteLineUuid: string;
    quotePriceUuid: string;
    priceStrategyConfigs: PriceStrategyConfigWithRelations[];
  },
) {
  const quoteLine = getQuoteLineOrThrow(state, params.quoteLineUuid);
  const quotePrice = getQuotePriceOrThrow(
    state,
    params.quoteLineUuid,
    params.quotePriceUuid,
  );
  const { marketCode } = state;

  if (
    !marketCode ||
    !quoteLine.marketPrices ||
    !quoteLine.productOptions ||
    !quoteLine.craftProductOptions
  ) {
    throw new Error('Could not calculate price');
  }

  const allowFallback = getAllowFallback(
    quotePrice.priceStrategy,
    params.priceStrategyConfigs,
  );

  const price = calculate(
    {
      quantity: quotePrice.quantity,
      marketCode,
      marketPrices: quoteLine.marketPrices,
      productOptions: Object.values(quoteLine.productOptions),
      craftProductOptions: quoteLine.craftProductOptions,
      allowFallback,
    },
    SnapRoundingOptionsCustom(state),
  );

  if (!price) {
    throw new Error('Could not calculate price');
  }

  const unitPrice = price.unitPrice;

  applyQuotePriceRecalcResult(state, {
    quoteLineUuid: params.quoteLineUuid,
    quotePriceUuid: params.quotePriceUuid,
    quotePrice,
    patch: {
      unitPrice,
      calculatedUnitPrice: unitPrice,
      suggestedPriceStatus: SnapSuggestionStatus.ACCEPTED,
      unitPriceAddition: price.unitPriceAddition ?? null,
      changesUnitPrice: price.changesUnitPrice,
      totalPrice: calculateTotal(
        unitPrice,
        quotePrice.quantity,
        SnapRoundingOptionsCustom(state),
      ),
    },
    rawFieldsToClear: ['unitPrice'],
  });

  return price;
}
